import React, { useState, useEffect } from 'react';
import supabase from '../../utils/supabaseUtils';
import './LeadManagement.css';

const LeadManagement = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadToEdit, setLeadToEdit] = useState(null);
  const [formValues, setFormValues] = useState({
    name: '',
    organization: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    status: 'new',
    type: 'team',
    notes: '',
    next_follow_up: ''
  });
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      // Mock lead data for development
      const mockLeads = [
        {
          id: '1',
          name: 'Denver Vikings',
          organization: 'Denver Vikings Football',
          contact_name: 'Sarah Johnson',
          contact_email: 'sarah.johnson@example.com',
          contact_phone: '(303) 555-1234',
          status: 'new',
          type: 'team',
          notes: 'Interested in season package for football team',
          next_follow_up: '2023-06-15',
          created_at: '2023-05-10T08:30:00Z',
          updated_at: '2023-05-10T08:30:00Z'
        },
        {
          id: '2',
          name: 'Colorado Rapids Youth Soccer',
          organization: 'Colorado Rapids',
          contact_name: 'Michael Rodriguez',
          contact_email: 'mrodriguez@example.com',
          contact_phone: '(720) 555-5678',
          status: 'contacted',
          type: 'club',
          notes: 'Sent initial proposal. Need to follow up on package options',
          next_follow_up: '2023-06-17',
          created_at: '2023-05-05T10:15:00Z',
          updated_at: '2023-05-12T14:30:00Z'
        },
        {
          id: '3',
          name: 'Boulder Basketball Tournament',
          organization: 'Boulder Basketball Club',
          contact_name: 'Amanda Chen',
          contact_email: 'achen@bolderbasketball.org',
          contact_phone: '(720) 555-2233',
          status: 'qualified',
          type: 'event',
          notes: 'Interested in full tournament coverage. Meeting scheduled to discuss details',
          next_follow_up: '2023-06-21',
          created_at: '2023-04-28T11:30:00Z',
          updated_at: '2023-05-08T09:45:00Z'
        },
        {
          id: '4',
          name: 'Eagles High School Athletics',
          organization: 'Eagles High School',
          contact_name: 'David Thompson',
          contact_email: 'dthompson@eagleshigh.edu',
          contact_phone: '(303) 555-3344',
          status: 'proposal',
          type: 'school',
          notes: 'Proposal sent for all sports coverage for the upcoming school year',
          next_follow_up: '2023-06-18',
          created_at: '2023-04-15T09:20:00Z',
          updated_at: '2023-05-02T13:10:00Z'
        },
        {
          id: '5',
          name: 'Colorado State Championship',
          organization: 'State Athletic Association',
          contact_name: 'Jessica Williams',
          contact_email: 'jwilliams@tournament.com',
          contact_phone: '(970) 555-9876',
          status: 'negotiation',
          type: 'event',
          notes: 'Negotiating final terms for exclusive photography rights',
          next_follow_up: '2023-06-10',
          created_at: '2023-03-20T14:45:00Z',
          updated_at: '2023-05-15T11:30:00Z'
        }
      ];
      
      setLeads(mockLeads);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Validate required fields
      if (!formValues.name || !formValues.organization) {
        alert("Name and organization are required");
        setLoading(false);
        return;
      }
      
      if (leadToEdit) {
        // Update existing lead
        const updatedLeads = leads.map(lead => 
          lead.id === leadToEdit.id 
            ? { 
                ...lead, 
                ...formValues,
                updated_at: new Date().toISOString()
              }
            : lead
        );
        setLeads(updatedLeads);
        
        // In production, you would update the lead in Supabase
      } else {
        // Create new lead
        const newLead = {
          id: crypto.randomUUID(),
          ...formValues,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        setLeads([newLead, ...leads]);
        
        // In production, you would insert the lead in Supabase
      }
      
      resetForm();
    } catch (error) {
      console.error('Error saving lead:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormValues({
      name: '',
      organization: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      status: 'new',
      type: 'team',
      notes: '',
      next_follow_up: ''
    });
    setLeadToEdit(null);
    setShowLeadForm(false);
  };

  const handleEditLead = (lead) => {
    setLeadToEdit(lead);
    setFormValues({
      name: lead.name,
      organization: lead.organization,
      contact_name: lead.contact_name,
      contact_email: lead.contact_email,
      contact_phone: lead.contact_phone,
      status: lead.status,
      type: lead.type,
      notes: lead.notes,
      next_follow_up: lead.next_follow_up
    });
    setShowLeadForm(true);
  };

  const handleDeleteLead = async (leadId) => {
    if (!confirm("Are you sure you want to delete this lead?")) {
      return;
    }
    
    try {
      // Remove from UI for immediate feedback
      setLeads(leads.filter(lead => lead.id !== leadId));
      
      // In production, you would delete from Supabase
      alert("Lead deleted successfully");
    } catch (error) {
      console.error("Error deleting lead:", error);
      alert(`Error: ${error.message}`);
      fetchLeads(); // Refresh to restore data
    }
  };

  const handleAddCommunication = (leadId) => {
    // In a full implementation, this would open a communication log entry form
    alert(`Add communication for lead ID: ${leadId}`);
  };

  // Filter leads by status and search term
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.organization.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.contact_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || lead.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Format status for display
  const getStatusLabel = (status) => {
    const statusMap = {
      'new': 'New',
      'contacted': 'Contacted',
      'qualified': 'Qualified',
      'proposal': 'Proposal',
      'negotiation': 'Negotiation',
      'closed_won': 'Closed (Won)',
      'closed_lost': 'Closed (Lost)'
    };
    
    return statusMap[status] || status;
  };

  // Get class for status badge
  const getStatusClass = (status) => {
    return `status-badge ${status}`;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="leads-container">
      <div className="section-header">
        <h3>Lead Management</h3>
        <div className="header-actions">
          <button 
            className="refresh-button"
            onClick={fetchLeads}
            title="Refresh Leads"
          >
            <i className="fas fa-sync-alt"></i>
          </button>
          <button 
            className="add-button"
            onClick={() => {
              setLeadToEdit(null);
              setFormValues({
                name: '',
                organization: '',
                contact_name: '',
                contact_email: '',
                contact_phone: '',
                status: 'new',
                type: 'team',
                notes: '',
                next_follow_up: ''
              });
              setShowLeadForm(true);
            }}
          >
            <i className="fas fa-plus-circle"></i> Add Lead
          </button>
        </div>
      </div>
      
      {/* Search and filter controls */}
      <div className="leads-controls">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button 
              className="clear-search" 
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        
        <div className="filter-controls">
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="proposal">Proposal</option>
            <option value="negotiation">Negotiation</option>
            <option value="closed_won">Closed (Won)</option>
            <option value="closed_lost">Closed (Lost)</option>
          </select>
        </div>
      </div>
      
      {/* Leads List */}
      {loading && leads.length === 0 ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading leads...</p>
        </div>
      ) : filteredLeads.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fas fa-funnel-dollar"></i>
          </div>
          <h3>No Leads Found</h3>
          <p>{searchTerm || filterStatus !== 'all' ? 'Try adjusting your search or filters' : 'Add your first lead to get started'}</p>
          {!searchTerm && filterStatus === 'all' && (
            <button 
              className="add-button"
              onClick={() => {
                setLeadToEdit(null);
                setFormValues({
                  name: '',
                  organization: '',
                  contact_name: '',
                  contact_email: '',
                  contact_phone: '',
                  status: 'new',
                  type: 'team',
                  notes: '',
                  next_follow_up: ''
                });
                setShowLeadForm(true);
              }}
            >
              <i className="fas fa-plus-circle"></i> Add Lead
            </button>
          )}
        </div>
      ) : (
        <div className="leads-table-container">
          <table className="leads-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Contact</th>
                <th>Status</th>
                <th>Next Follow-up</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map(lead => (
                <tr key={lead.id}>
                  <td className="lead-info">
                    <div className="lead-name">{lead.name}</div>
                    <div className="lead-org">{lead.organization}</div>
                  </td>
                  <td>
                    <div className="contact-info">
                      <div>{lead.contact_name}</div>
                      <div className="contact-details">
                        {lead.contact_email && <span><i className="fas fa-envelope"></i> {lead.contact_email}</span>}
                        {lead.contact_phone && <span><i className="fas fa-phone"></i> {lead.contact_phone}</span>}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={getStatusClass(lead.status)}>
                      {getStatusLabel(lead.status)}
                    </span>
                  </td>
                  <td>
                    {lead.next_follow_up ? (
                      <div className={`follow-up-date ${new Date(lead.next_follow_up) < new Date() ? 'overdue' : ''}`}>
                        <i className="fas fa-calendar-alt"></i> {formatDate(lead.next_follow_up)}
                      </div>
                    ) : (
                      <span className="no-date">No follow-up scheduled</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-button comm"
                        onClick={() => handleAddCommunication(lead.id)}
                        title="Log Communication"
                      >
                        <i className="fas fa-comment-dots"></i>
                      </button>
                      <button 
                        className="action-button edit"
                        onClick={() => handleEditLead(lead)}
                        title="Edit Lead"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button 
                        className="action-button delete"
                        onClick={() => handleDeleteLead(lead.id)}
                        title="Delete Lead"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Lead Form Modal */}
      {showLeadForm && (
        <div className="modal-overlay">
          <div className="lead-form-modal">
            <div className="modal-header">
              <h3>{leadToEdit ? 'Edit Lead' : 'Add New Lead'}</h3>
              <button 
                className="close-button"
                onClick={resetForm}
                disabled={loading}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Lead Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formValues.name}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    placeholder="Team or opportunity name"
                  />
                </div>
                
                <div className="form-group">
                  <label>Organization</label>
                  <input
                    type="text"
                    name="organization"
                    value={formValues.organization}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    placeholder="Organization or company name"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Contact Name</label>
                  <input
                    type="text"
                    name="contact_name"
                    value={formValues.contact_name}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label>Lead Type</label>
                  <select
                    name="type"
                    value={formValues.type}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="team">Team</option>
                    <option value="club">Club</option>
                    <option value="school">School</option>
                    <option value="event">Event</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Contact Email</label>
                  <input
                    type="email"
                    name="contact_email"
                    value={formValues.contact_email}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="text"
                    name="contact_phone"
                    value={formValues.contact_phone}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    name="status"
                    value={formValues.status}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                    <option value="proposal">Proposal</option>
                    <option value="negotiation">Negotiation</option>
                    <option value="closed_won">Closed (Won)</option>
                    <option value="closed_lost">Closed (Lost)</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Next Follow-up Date</label>
                  <input
                    type="date"
                    name="next_follow_up"
                    value={formValues.next_follow_up}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formValues.notes}
                  onChange={handleInputChange}
                  rows="3"
                  disabled={loading}
                  placeholder="Enter any additional information or notes about this lead"
                ></textarea>
              </div>
              
              <div className="form-buttons">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="loading-text">
                      <span className="spinner-small"></span>
                      {leadToEdit ? 'Updating...' : 'Creating...'}
                    </span>
                  ) : (
                    leadToEdit ? 'Update Lead' : 'Create Lead'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadManagement; 